import { z } from 'zod';
import { Confidence, EntityLabel } from './entities.js';
/**
 * What kind of graph-review item this candidate is. The original ER
 * scaffolding only had `'alias'` (alias / dedup); issue #36's
 * streamliner agent broadens the queue to four kinds.
 *
 *   - alias              an observed name that may refer to a known entity
 *   - lowConfidenceFact  an existing edge whose stored confidence is thin
 *                        and warrants analyst review
 *   - missingEdge        two entities co-mentioned in many docs but with
 *                        no direct edge — analyst confirms or rejects
 *   - redundantSchema    two predicate types that look semantically
 *                        equivalent (e.g. FOUNDED_BY ↔ FOUNDER_OF). Promotion
 *                        is informational; renames happen out-of-band via
 *                        an admin script (`pnpm rename-predicate FROM TO`)
 *                        because predicate renames have cross-query blast
 *                        radius and shouldn't auto-fire from the queue.
 *
 * Default is `'alias'` for back-compat with existing pre-#36 candidates.
 */
export const CandidateKind = z.enum([
    'alias',
    'lowConfidenceFact',
    'missingEdge',
    'redundantSchema',
]);
/**
 * Probabilistic, non-merging graph-review item (product spec §6.5,
 * extended in issue #36).
 *
 * Two records for "Trita Parsi" resolve to the same canonical node with
 * confidence 0.99 and the original records remain visible. A record for
 * "T. Parsi" in a 2001 cable resolves at 0.62 and stays as a linked
 * candidate until promoted by an analyst. Promotion never merges nodes —
 * it only flips the candidate's status, preserving full lineage so the
 * original observation remains queryable. The same review-then-decide
 * model applies to the other three streamliner kinds.
 *
 * `payload` carries kind-specific evidence (e.g. for `missingEdge`:
 * suggested predicate + co-mention count). Stored as a JSON-serialized
 * string in Neo4j; deserialized on read. The shape is loose at the
 * schema level so detectors can evolve without schema migrations;
 * `payloadShapes` below documents the contract per kind.
 */
export const CandidateRecord = z.object({
    id: z.string(),
    /** Raw observed name from the source (e.g. "T. Parsi"). For non-alias
     *  kinds this is a synthesized label (e.g. "FOUNDED_BY ↔ FOUNDER_OF"
     *  for `redundantSchema`) that lets the existing list UI render
     *  without special-casing. */
    observedName: z.string(),
    /** Source context — docId, quote, etc. */
    sourceDocId: z.string().optional(),
    /** The canonical entity we think this refers to, if any. */
    candidateFor: z.string().optional(),
    /** 0.0–1.0 match / review confidence. */
    confidence: Confidence,
    /** Why the system thinks this is a match. Human-readable. Streamliner
     *  derivations always start with `streamliner: <kind>:` so the queue
     *  can group/filter by source. */
    derivation: z.string().optional(),
    label: EntityLabel.default('Entity'),
    status: z.enum(['pending', 'promoted', 'rejected']).default('pending'),
    reviewedByUid: z.string().optional(),
    reviewedAt: z.string().optional(),
    /** Kind of review item. Defaults to `'alias'` for back-compat with
     *  pre-#36 candidates that don't carry the field. */
    kind: CandidateKind.default('alias'),
    /** Kind-specific evidence bag. See `payloadShapes` for the contract. */
    payload: z.record(z.string(), z.unknown()).optional(),
    /** Deterministic dedupe key the streamliner detectors use to avoid
     *  re-emitting the same proposal on every run. Computed from
     *  `kind` + canonicalized payload (e.g. for `missingEdge`,
     *  `me:${[a,b].sort().join('|')}`). Only present on streamliner-
     *  created candidates; alias candidates omit it (the existing
     *  `MAY_BE_SAME_AS` edge serves as the dedupe scaffold). */
    dedupeKey: z.string().optional(),
});
/**
 * Build the deterministic dedupe key the streamliner persists on
 * `:Candidate` nodes (issue #36). Order-independent for symmetric
 * pairs (missingEdge, redundantSchema). Returns `undefined` for
 * `alias` kinds — those use the `MAY_BE_SAME_AS` edge as their
 * dedupe scaffold and don't need a key.
 *
 * Co-located with `CandidateRecord` so server detectors and Cypher
 * call sites read from the same source.
 */
export function dedupeKeyFor(kind, payload) {
    if (!payload)
        return undefined;
    if (kind === 'lowConfidenceFact') {
        return `lcf:${String(payload.subject ?? '')}|${String(payload.predicate ?? '')}|${String(payload.object ?? '')}`;
    }
    if (kind === 'missingEdge') {
        const a = String(payload.subject ?? '');
        const b = String(payload.object ?? '');
        const [lo, hi] = a < b ? [a, b] : [b, a];
        return `me:${lo}|${hi}`;
    }
    if (kind === 'redundantSchema') {
        const a = String(payload.predicateA ?? '');
        const b = String(payload.predicateB ?? '');
        const [lo, hi] = a < b ? [a, b] : [b, a];
        return `rs:${lo}|${hi}`;
    }
    return undefined;
}
/**
 * Documented shape of `payload` per kind. Detectors construct payloads
 * matching these contracts; the UI narrows on `kind` to render
 * kind-specific cards.
 */
export const payloadShapes = {
    alias: {
    /* No additional fields — observedName + candidateFor + sourceDocId
     * on the parent suffice. */
    },
    lowConfidenceFact: {
        subject: 'string',
        predicate: 'string',
        object: 'string',
        currentConfidence: 'number',
        supportingDocIds: 'string[]',
    },
    missingEdge: {
        subject: 'string',
        object: 'string',
        suggestedPredicate: 'string',
        coMentioningDocCount: 'number',
        supportingDocIds: 'string[]',
    },
    redundantSchema: {
        predicateA: 'string',
        predicateB: 'string',
        reason: 'string',
        occurrencesA: 'number',
        occurrencesB: 'number',
    },
};
/**
 * Body accepted by `POST /api/candidates`. The server assigns `id`,
 * defaults `status` to `'pending'`, and leaves `reviewedByUid` /
 * `reviewedAt` unset until an analyst acts.
 */
export const CreateCandidateInput = CandidateRecord.omit({
    id: true,
    status: true,
    reviewedByUid: true,
    reviewedAt: true,
});
